import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, Link2, Scale, Bell, X } from "lucide-react";

const SUGGESTED = [
  "Will Bitcoin trade above $150,000 by Dec 31?",
  "Who wins the 2028 US presidential election?",
  "Will the Lakers win their next game?",
];

export default function OnboardingFlow() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (isLoading || !user) return;
    (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("onboarding_complete")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!cancelled && data && data.onboarding_complete === false) setOpen(true);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [user, isLoading]);

  const dismiss = async (markComplete: boolean) => {
    setOpen(false);
    if (user && markComplete) {
      try {
        await supabase.from("profiles").update({ onboarding_complete: true }).eq("user_id", user.id);
      } catch { /* noop */ }
    }
  };

  const startQuery = (q: string) => {
    const target = q.trim();
    if (!target) return;
    void dismiss(true);
    navigate(`/?q=${encodeURIComponent(target)}&autorun=1`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => dismiss(false)}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-border/60 bg-card shadow-2xl p-5"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-primary/12 border border-primary/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-display font-bold text-foreground">Welcome to Decyzon</p>
                  <p className="text-[11px] text-muted-foreground font-body">The decision layer for prediction markets</p>
                </div>
              </div>
              <button onClick={() => dismiss(false)} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <ol className="space-y-1.5 mb-4">
              {[
                { icon: Link2, label: "Paste a market or ask a question" },
                { icon: Scale, label: "Get the verdict, edge, and trap" },
                { icon: Bell, label: "Watch and re-check on shifts" },
              ].map((s, i) => (
                <li key={i} className="flex items-center gap-2.5 text-xs font-body text-muted-foreground">
                  <span className="w-5 h-5 rounded-full bg-muted/40 text-foreground flex items-center justify-center text-[10px] font-display">{i + 1}</span>
                  <s.icon className="w-3.5 h-3.5 text-primary/80" />
                  <span>{s.label}</span>
                </li>
              ))}
            </ol>

            <form
              onSubmit={(e) => { e.preventDefault(); startQuery(query); }}
              className="mb-3"
            >
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Paste market URL or ask a question"
                className="w-full px-3 py-2.5 rounded-lg bg-secondary border border-border/60 text-sm font-body text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/40 transition-colors"
                autoFocus
              />
              <button
                type="submit"
                disabled={!query.trim()}
                className="mt-2 w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-body font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
              >
                Run my first decision
              </button>
            </form>

            <div className="space-y-1.5">
              <p className="text-[10px] text-muted-foreground/60 font-body uppercase tracking-wider">Or try one</p>
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => startQuery(s)}
                  className="w-full text-left px-3 py-2 rounded-lg bg-secondary/60 border border-border/40 text-xs font-body text-foreground hover:border-primary/30 hover:bg-primary/5 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>

            <button
              onClick={() => dismiss(true)}
              className="mt-3 w-full py-1.5 text-[11px] text-muted-foreground font-body hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
